import { ApiKey } from "../../state/types";
// ============================================
// API Key State Utilities
// ============================================

/**
 * Check if an API key is revoked (soft-deleted).
 * Revoked keys are NOT visible to users and cannot be recovered.
 * Only the new `revokedAt` timestamp indicates soft-delete.
 *
 * @param {object} apiKey - The API key document
 * @returns {boolean}
 */
function isApiKeyRevoked(apiKey: ApiKey) {
    return !!apiKey?.revokedAt;
}

/**
 * Check if an API key is disabled.
 * Disabled keys ARE visible to users and can be re-enabled.
 * Supports both legacy `revoked: true` and new `disabledAt` timestamp.
 *
 * @param {object} apiKey - The API key document
 * @returns {boolean}
 */
function isApiKeyDisabled(apiKey: ApiKey) {
    return !!(apiKey?.disabledAt || apiKey?.revoked);
}

/**
 * Check if an API key is active (usable for API calls).
 * A key is active if it's neither revoked nor disabled.
 *
 * @param {object} apiKey - The API key document
 * @returns {boolean}
 */
function isApiKeyActive(apiKey: ApiKey) {
    return !!apiKey && !isApiKeyRevoked(apiKey) && !isApiKeyDisabled(apiKey);
}

/**
 * Check if an API key should be visible to users.
 * Revoked (soft-deleted) keys are hidden; disabled keys are visible.
 *
 * @param {object} apiKey - The API key document
 * @returns {boolean}
 */
function isApiKeyVisibleToUser(apiKey: ApiKey) {
    return !!apiKey && !isApiKeyRevoked(apiKey);
}

export { isApiKeyRevoked, isApiKeyDisabled, isApiKeyActive, isApiKeyVisibleToUser };
